import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { DeviceType_Enum, ListDevice } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { Ringtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DevicesQueries } from '@frontend/api-devices';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { validateName } from '@frontend/validation';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  Heading,
  SkeletonLoaders,
  useAlert,
  useForm,
  Text,
  TextField,
  useTooltip,
  Info,
} from '@frontend/design-system';
import { RingtoneDropdown, type RingtoneOption } from './atoms/ringtone-dropdown/ringtone-dropdown';
import { Step } from './atoms/step';
import { CallerLabelBaseText, getRemainingCharacters } from './characters-helpers';
import { AddStepPanelProps } from './types';

type GroupedDevices = {
  linkedDevice?: ListDevice;
  softphones: ListDevice[];
  deskPhones: ListDevice[];
  mobileApps: (ListDevice & { createdAt?: string })[];
};

export const DeviceStep = ({
  initialState,
  callObjectsData,
  callRoute,
  isDeviceExtensionRoute = false,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const { devices = [], devicesIsLoading } = callObjectsData;

  const linkedDeviceId = callRoute.deviceExtension?.deviceId;

  const groupedDevices = useMemo(() => {
    const grouped = devices.reduce<GroupedDevices>(
      (acc, device) => {
        if (isDeviceExtensionRoute && device.deviceId === linkedDeviceId) {
          acc.linkedDevice = device;
        } else if (device.type === DeviceType_Enum.SOFTPHONE) {
          acc.softphones.push(device);
        } else if (device.type === DeviceType_Enum.DESK_PHONE) {
          acc.deskPhones.push(device);
        } else if (device.type === DeviceType_Enum.MOBILE_APP) {
          const createdAtValue = device.sipProfiles?.[0]?.createdAt;
          acc.mobileApps.push({
            ...device,
            createdAt: createdAtValue ? formatters.date(createdAtValue) : undefined,
          });
        }

        return acc;
      },
      { softphones: [], deskPhones: [], mobileApps: [] }
    );
    return grouped;
  }, [devices]);

  const { data: ringtones = [] } = DevicesQueries.useRingtones(
    {},
    { select: (data) => data.ringtones ?? [], retry: false }
  );

  const { getFieldProps, isComplete, values, seedValues } = useForm({
    fields: {
      deviceId: {
        required: true,
        type: 'dropdown',
        value: initialState?.callObject?.primitiveId,
      },
      ringDuration: {
        type: 'dropdown',
        required: true,
        value: String(initialState?.callObject?.deviceExpansion?.timeout ?? '18000'),
      },
      callerLabel: {
        required: false,
        type: 'text',
        value: initialState?.callObject?.deviceExpansion?.callerLabel,
        validator: validateName,
        validateOnChange: true,
      },
      ringtone: {
        type: 'dropdown',
        required: false,

        value: initialState?.callObject?.deviceExpansion?.ringtone?.ringtoneType ?? Ringtone.RINGTONE_UNSPECIFIED,
      },
    },
  });

  const selectedDevice = useMemo(() => {
    return devices.find((device) => device.deviceId === values.deviceId);
  }, [devices, values.deviceId]);

  const isDeskPhone = selectedDevice?.type === DeviceType_Enum.DESK_PHONE;

  // Build the list of ringtones to show for the selected desk phone using a two-pass
  // strategy: model-specific ringtones take priority; generic (models=[]) ones are
  // included only when no model-specific match exists for the same ringtoneType.
  // Returns an empty array when no desk phone is selected (the dropdown is hidden anyway).
  const filteredRingtones: RingtoneOption[] = useMemo(() => {
    if (!selectedDevice || !isDeskPhone) return [];

    const sortSilentFirst = (a: RingtoneOption, b: RingtoneOption) => {
      if (a.ringtoneType === Ringtone.RINGTONE_SILENT) return -1;
      if (b.ringtoneType === Ringtone.RINGTONE_SILENT) return 1;
      return 0;
    };

    const deviceMake = selectedDevice.make;
    const deviceModel = selectedDevice.model;

    // If the make is unknown, fall back to generic Yealink ringtones (make="yealink", models=[]).
    if (!deviceMake || deviceMake.toLowerCase() === 'unspecified') {
      return ringtones
        .filter((ringtone) => ringtone.make.toLowerCase() === 'yealink' && (ringtone.models?.length ?? 0) === 0)
        .sort(sortSilentFirst);
    }

    // First pass: find ringtones that match by specific model
    const modelMatched = ringtones.filter(
      (ringtone) =>
        (ringtone.models?.length ?? 0) > 0 &&
        ringtone.make === deviceMake &&
        deviceModel &&
        ringtone.models?.some((m) => m.toLowerCase() === deviceModel.toLowerCase())
    );

    // Collect ringtoneTypes that already have a model-specific match
    const modelMatchedTypes = new Set(modelMatched.map((r) => r.ringtoneType));

    // Second pass: include generic ringtones (empty models array) only if no model-specific match exists for the same ringtoneType
    const genericMatched = ringtones.filter(
      (ringtone) =>
        (ringtone.models?.length ?? 0) === 0 &&
        !modelMatchedTypes.has(ringtone.ringtoneType) &&
        ringtone.make === deviceMake
    );

    return [...modelMatched, ...genericMatched].sort(sortSilentFirst);
  }, [ringtones, selectedDevice, isDeskPhone]);

  // When the user selects a different device, reset the ringtone to RINGTONE_UNSPECIFIED so
  // that the effect below resolves it to the new device's default option.
  const prevDeviceIdRef = useRef(values.deviceId);
  useEffect(() => {
    const prevDeviceId = prevDeviceIdRef.current;
    prevDeviceIdRef.current = values.deviceId;

    // Skip the initial mount — only reset when the user actively changes the device.
    if (prevDeviceId === values.deviceId) return;

    seedValues({ ringtone: Ringtone.RINGTONE_UNSPECIFIED });
  }, [values.deviceId, seedValues]);

  // This effect has three responsibilities:
  // 1. Non-desk-phone selected: reset to RINGTONE_UNSPECIFIED (the ringtone dropdown is hidden,
  //    so we use RINGTONE_UNSPECIFIED as the "nothing saved" value).
  // 2. Resolve RINGTONE_UNSPECIFIED on load: when the stored/initial value is RINGTONE_UNSPECIFIED
  //    (API's way of saying "use device default") and we now have a filtered ringtone list,
  //    resolve it to the ringtoneType of the item with isDefault=true so the dropdown
  //    pre-selects the correct existing option.  On save, handleProceedClick maps it back.
  // 3. Device make/model change: if the currently selected ringtoneType is no longer present
  //    in the new filtered list, fall back to RINGTONE_UNSPECIFIED which will then be
  //    resolved to the new device's default in the next effect run.
  useEffect(() => {
    if (!isDeskPhone) {
      seedValues({ ringtone: Ringtone.RINGTONE_UNSPECIFIED });
    } else if (values.ringtone === Ringtone.RINGTONE_UNSPECIFIED && filteredRingtones.length > 0) {
      // Resolve the stored "use default" value to the actual default ringtoneType so the
      // dropdown can highlight an existing named option rather than showing nothing selected.
      const defaultRingtone = filteredRingtones.find((r) => r.isDefault);
      if (defaultRingtone) {
        seedValues({ ringtone: defaultRingtone.ringtoneType });
      }
    } else if (
      values.ringtone &&
      values.ringtone !== Ringtone.RINGTONE_UNSPECIFIED &&
      filteredRingtones.length > 0 &&
      !filteredRingtones.some((r) => r.ringtoneType === values.ringtone)
    ) {
      seedValues({ ringtone: Ringtone.RINGTONE_UNSPECIFIED });
    }
  }, [isDeskPhone, filteredRingtones, values.ringtone, seedValues]);

  const handleProceedClick = useCallback(() => {
    if (!selectedDevice) {
      alerts.error('Unknown device data');
      sentry.error({
        error: 'Could not find device by ID in DeviceStep',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            deviceId: values.deviceId,
          },
        },
      });
      return;
    }

    const callerLabel = selectedDevice.type === DeviceType_Enum.MOBILE_APP ? '' : values.callerLabel;

    // Use filteredRingtones (already filtered to the selected device's make/model) to find
    // the selected ringtone. Using the unfiltered `ringtones` array would risk matching a
    // same-ringtoneType entry from a different make that may have a different isDefault value.
    const selectedRingtone = filteredRingtones.find((r) => r.ringtoneType === values.ringtone);

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE,
      callObject: {
        primitiveId: selectedDevice.deviceId,
        primitiveName: selectedDevice.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        deviceExpansion: {
          timeout: Number(values.ringDuration),
          callerLabel,
          deviceType: selectedDevice.type,
          // When the selected ringtone is the device's default (isDefault=true), we store
          // RINGTONE_UNSPECIFIED in the API payload.  This is the API contract for "use the
          // device's built-in default" — the actual ringtoneType is only an internal display
          // detail resolved from filteredRingtones in the useEffect above.
          ringtone: selectedRingtone
            ? {
                ringtoneType: selectedRingtone.isDefault
                  ? Ringtone.RINGTONE_UNSPECIFIED
                  : selectedRingtone.ringtoneType,
                displayName: selectedRingtone.displayName,
              }
            : undefined,
        },
      },
    });
  }, [
    selectedDevice,
    values.deviceId,
    values.ringDuration,
    values.callerLabel,
    values.ringtone,
    filteredRingtones,
    alerts,
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
  ]);

  return (
    <Step title={t('Device')} primaryButtonDisabled={!isComplete} onPrimaryButtonClick={handleProceedClick}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(3)};
          max-width: 318px;
        `}
      >
        <SkeletonLoaders.Loader isLoading={devicesIsLoading} shape='rectangle' height={50}>
          <DropdownField label={t('Select Device')} {...getFieldProps('deviceId')} placeholder={t('Select Device')}>
            {!!groupedDevices.linkedDevice && (
              <DropdownField.Option
                key={groupedDevices.linkedDevice.deviceId}
                value={groupedDevices.linkedDevice.deviceId}
                tooltipPlacement='right'
                displayValue={groupedDevices.linkedDevice.name}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${theme.spacing(0.5)};
                  `}
                >
                  <Text as={'span'}>{groupedDevices.linkedDevice.name}</Text>
                  <Text as={'span'} size='small' color='subdued'>
                    {t('Linked Device')}
                  </Text>
                </div>
              </DropdownField.Option>
            )}

            {groupedDevices.deskPhones.length > 0 && (
              <DropdownField.OptionGroup label={t('Desk Phones')}>
                {groupedDevices.deskPhones.map((device) => (
                  <DropdownField.Option key={device.deviceId} value={device.deviceId} tooltipPlacement='right'>
                    {device.name}
                  </DropdownField.Option>
                ))}
              </DropdownField.OptionGroup>
            )}

            {groupedDevices.softphones.length > 0 && (
              <DropdownField.OptionGroup label={t('Softphones')}>
                {groupedDevices.softphones.map((device) => (
                  <DropdownField.Option key={device.deviceId} value={device.deviceId} tooltipPlacement='right'>
                    {device.name}
                  </DropdownField.Option>
                ))}
              </DropdownField.OptionGroup>
            )}

            {groupedDevices.mobileApps.length > 0 && (
              <DropdownField.OptionGroup label={t('Mobile Apps')}>
                {groupedDevices.mobileApps.map((device) => (
                  <DropdownField.Option
                    key={device.deviceId}
                    value={device.deviceId}
                    displayValue={device.name}
                    css={css`
                      /*  We need to remove the padding here to add it in the MobileAppOption component so that the
                        tooltip trigger area includes the padding there */
                      padding: 0 !important;
                      p {
                        /* Since we are using a custom component for this DropdownField.Option, we need to handle our
                          own text overflow to show ellipsis */
                        overflow: hidden; // This will disable the default tooltip used by DropdownField.Option
                        text-overflow: ellipsis; // This will enable the ellipsis effect in our <p> elements in our custom component
                      }
                    `}
                  >
                    <MobileAppOption key={device.deviceId} device={device} />
                  </DropdownField.Option>
                ))}
              </DropdownField.OptionGroup>
            )}
          </DropdownField>
        </SkeletonLoaders.Loader>

        {!(selectedDevice?.type === DeviceType_Enum.MOBILE_APP) && (
          <SkeletonLoaders.Loader isLoading={devicesIsLoading} shape='rectangle' height={50}>
            <TextField
              label={t('Caller Label (Optional)')}
              {...getFieldProps('callerLabel')}
              helperText={getRemainingCharacters(values.callerLabel)}
            />
            <CallerLabelBaseText value={values.callerLabel} error={getFieldProps('callerLabel').error} />
          </SkeletonLoaders.Loader>
        )}
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <Heading level={3}>{t('Ring Settings')}</Heading>
      </div>
      <SkeletonLoaders.Loader isLoading={devicesIsLoading} shape='rectangle' width={320} height={50}>
        <div>
          <DropdownField
            label={t('Ring Duration')}
            {...getFieldProps('ringDuration')}
            placeholder={t('Ring Duration')}
            css={css`
              max-width: 318px;
              margin-top: ${theme.spacing(1)};
            `}
          >
            {[6_000, 12_000, 18_000, 24_000, 30_000, 36_000, 42_000, 48_000, 54_000, 60_000].map((duration) => (
              <DropdownField.Option key={duration} value={String(duration)}>
                {`${t('{{duration}} seconds', { duration: duration / 1000 })}`}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(0.5)};
              padding-top: ${theme.spacing(1)};
            `}
          >
            <Text
              as='span'
              size='small'
              color='subdued'
              css={css`
                font-size: ${theme.fontSize(12)};
                line-height: 1.33;
              `}
            >
              {t('6 seconds usually equals 1 ring')}
            </Text>
            <Info color='subdued' trigger='hover'>
              {t(
                'The length of time a call rings before the system takes the caller to the next step in the Call Route.'
              )}
            </Info>
          </div>
        </div>
      </SkeletonLoaders.Loader>

      {isDeskPhone && (
        <SkeletonLoaders.Loader isLoading={devicesIsLoading} shape='rectangle' width={320} height={50}>
          <div
            css={css`
              max-width: 318px;
              margin-top: ${theme.spacing(1)};
              position: relative; /* This is needed to position the "New" badge */
            `}
          >
            <RingtoneDropdown {...getFieldProps('ringtone')} ringtones={filteredRingtones} />

            {/* Temporary badge for new feature */}
            <span
              css={css`
                display: inline-flex;
                position: absolute;
                top: 8px;
                right: -52px;
              `}
            ></span>
          </div>
        </SkeletonLoaders.Loader>
      )}
    </Step>
  );
};

const MobileAppOption = ({ device }: { device: GroupedDevices['mobileApps'][number] }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'right' });
  const { t } = useTranslation('phone');

  const displayCreatedAt = device.createdAt || t('Unknown');

  return (
    <>
      <div
        {...triggerProps}
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(0.5)};
          padding: ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(4)};
        `}
      >
        <Text>{device.name}</Text>
        <Text size='small'>{t('Created: {{createdAt}}', { createdAt: displayCreatedAt })}</Text>
      </div>

      <Tooltip
        {...tooltipProps}
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(0.5)};
        `}
      >
        <Text color='white'>
          {device.name}, {device.sipProfiles?.[0]?.name ?? 'unknown SIP name'}
        </Text>
        <Text size='small' color='white'>
          {t('Created: {{createdAt}}', { createdAt: displayCreatedAt })}
        </Text>
      </Tooltip>
    </>
  );
};
